import { observer } from 'mobx-react';
import BestPriceAvailabilitySearchStore from './BestPriceAvailabilitySearchStore';
import PackageSearchParams from '../PackageSearch/PackageSearchParams';
import useTranslation from '../../Hooks/useTranslation';
import fallback from '../../../_Translations/generated/PackageDropdownFilter.en.t.json';
import Keys from '../../../_Translations/generated/PackageDropdownFilter.en.t.json.keys';
import React, { JSX, RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { Element as ScrollTo } from 'react-scroll';
import {
  ApiAirportType,
  ApiBaseData,
  ApiGeoUnit,
  ApiItemType,
  ApiRentalCarOffice,
  ApiRoomContainer
} from '@ibe/api';
import DropdownFilter, {
  DropdownFilterComponent,
  Pill
} from '../../Components/DropdownFilter/DropdownFilter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import GeoUnitCheckboxAsync from '../../Components/Input/GeoUnitCheckboxAsync/GeoUnitCheckboxAsync';
import DateRangeInputSingleField, {
  DateRangeInputSingleFieldRefType
} from '../../Components/Input/DateRangeInputSingleField/DateRangeInputSingleField';
import useConfig from '../../Hooks/useConfig';
import useAppService from '../../Hooks/useAppService';
import { TFunction } from 'react-i18next';
import { AppService, ConfigModel } from '@ibe/services';
import { PaxSelector } from '../PackageSearch/SearchComponents';
import { getFormattedDate } from '../../Components/Input/DateRangeInputSingleField/DateRangeInputSingleFieldPopover';
import NumberSelector from '../../Components/Input/NumberSelector/NumberSelector';
import BusStopsCheckboxAsync from '../../Components/Input/BusStopsCheckboxAsync/BusStopsCheckboxAsync';
import RentalCarOfficeWithDistanceWrapper from '../../../Models/RentalCarOfficeWithDistanceWrapper';

export type PackageCode = { code: string; groupSize: string };
export const busStationsAnchor = 'busStationsAnchor';

const getPills = (
  store: BestPriceAvailabilitySearchStore,
  t: TFunction,
  config: ConfigModel,
  appService: AppService
) => {
  let pillItems: Pill[] = [];
  if (!!store.searchParams.occupancy && store.searchParams.occupancy?.length > 0) {
    pillItems = [
      {
        id: '1',
        label: `${store.searchParams.occupancy.reduce(
          (total, current) => total + current.adults || 0,
          0
        )} ${t(Keys.participants)}`,
        resetProp: 'occupancy'
      }
    ];
  }
  if (!!store.searchParams.origins && store.searchParams.origins.length > 0) {
    pillItems = [
      ...pillItems,
      {
        id: '2',
        label: store.searchParams.origins.map(origin => origin.name).join(', '),
        isClickable: true,
        resetProp: 'origins'
      }
    ];
  }
  if (
    !!store.searchParams.startDate &&
    store.searchParams.startDate !== 'Invalid Date' &&
    !!store.searchParams.endDate &&
    store.searchParams.endDate !== 'Invalid Date'
  ) {
    pillItems = [
      ...pillItems,
      {
        id: '3',
        label: `${t(Keys.travelPeriod)}: ${getFormattedDate(
          config,
          appService,
          new Date(store.fullStartDate)
        )} - ${getFormattedDate(config, appService, new Date(store.fullEndDate))}`,
        isClickable: true,
        resetProp: 'datepicker'
      }
    ];
  }
  return pillItems;
};

const PackageDropdownFilterRenderer = observer(function PackageDropdownFilterRenderer<T>({
  store,
  onChange,
  datepickerMinDate,
  datepickerMaxDate,
  origins: originsProp,
  ownArrivalsAndDepartures,
  disabled,
  defaultDate,
  dateRangeRef,
  onToggle,
  numberOfTravelers,
  memoDeps,
  showNumberSelector,
  isBusPackage,
  packageCodes
}: {
  store: BestPriceAvailabilitySearchStore;
  onChange: (searchParams: Partial<PackageSearchParams>) => void;
  datepickerMinDate?: string;
  datepickerMaxDate?: string;
  origins: string[];
  ownArrivalsAndDepartures?: boolean;
  disabled?: boolean;
  defaultDate?: {
    startDate?: string;
    endDate?: string;
  };
  dateRangeRef?: RefObject<DateRangeInputSingleFieldRefType>;
  onToggle?: {
    calendar?: (a: { isOpen?: boolean; startDate?: Date; endDate?: Date }) => void;
  };
  numberOfTravelers?: number;
  memoDeps?: T[];
  showNumberSelector?: boolean;
  isBusPackage?: boolean;
  packageCodes?: PackageCode[];
}): JSX.Element {
  const { t } = useTranslation('PackageDropdownFilter', fallback, Keys);
  const config = useConfig();
  const appService = useAppService();
  const [selectedPostCode, setSelectedPostCode] = useState<ApiBaseData>();
  const [isBusStationsOpen, setIsBusStationsOpen] = useState<boolean>(false);
  const [selectedBusStations, setSelectedBusStations] = useState<
    RentalCarOfficeWithDistanceWrapper[]
  >([]);

  useEffect(() => {
    if (isNumber(numberOfTravelers)) {
      store.setOccupancy([
        { adults: numberOfTravelers, children: 0, infants: 0, childrenAges: [], infantsAges: [] }
      ]);
    }
  }, [numberOfTravelers]);

  useEffect(() => {
    if (isBusPackage && defaultDate) {
      store.setStartDate(defaultDate.startDate ? defaultDate.startDate : '');
      store.setEndDate(defaultDate.endDate ? defaultDate.endDate : '');
    }
  }, [isBusPackage]);

  const resetFunctions: { [key: string]: () => void } = useMemo(() => {
    return {
      occupancy: () => {
        store.setOccupancy([]);
        onChange(store.searchParams);
      },
      postalCode: () => {
        store.setPostalCode(undefined);
      },
      busStations: () => {
        store.setBusStations(undefined);
      },
      origins: () => {
        store.setOrigins([]);
        onChange(store.searchParams);
      },
      datepicker: () => {
        store.setStartDate(null);
        store.setEndDate(null);
        onChange(store.searchParams);
      }
    };
  }, [store, ...(!!memoDeps ? [...memoDeps] : [])]);

  const setFunctions = useMemo(() => {
    return {
      postalCode: (postalCode: string) => {
        store.setPostalCode(postalCode);
      },
      busStations: (busStations: ApiRentalCarOffice[]) => {
        store.setBusStations(busStations);
        onChange(store.searchParams);
      },
      loadBusStations: (busStations: ApiRentalCarOffice[]) => {
        store.setBusStationsAvailable(busStations && busStations.length > 0);
      },
      occupancy: (occupancy: ApiRoomContainer[]) => {
        store.setOccupancy(occupancy);
        if (!isBusPackage) {
          onChange(store.searchParams);
        }
      },
      origins: (origins: ApiGeoUnit[]) => {
        store.setOrigins(origins);
        onChange(store.searchParams);
      },
      datepicker: (dates: [string | undefined, string | undefined]) => {
        if (!!dates[0]) {
          store.setStartDate(dates[0]);
        }
        if (!!dates[1]) {
          store.setEndDate(dates[1]);
        }
        if (!isBusPackage) {
          onChange(store.searchParams);
        }
      }
    };
  }, [store, ...(!!memoDeps ? [...memoDeps] : [])]);

  const handlePillClick = useCallback(
    (id: string): void => {
      const foundPillItem = getPills(store, t, config, appService).find(pill => pill.id === id);
      if (!!foundPillItem) {
        resetFunctions[foundPillItem.resetProp || '']();
      }
    },
    [resetFunctions, store, ...(!!memoDeps ? [...memoDeps] : [])]
  );

  const postalCodeSelectorComponent: DropdownFilterComponent = {
    id: '0',
    component: (
      <div
        className={`dropdown-filter__postalCode position-relative${
          disabled ? ' dropdown-filter__postalCode--disabled' : ''
        }`}
      >
        <ScrollTo name={busStationsAnchor}>
          <BusStopsCheckboxAsync
            setSelectedOptions={setSelectedBusStations}
            selectedOptions={selectedBusStations}
            packageCodes={packageCodes}
            store={store}
            value={store.busStations}
            onLoadStations={setFunctions.loadBusStations}
            withSelectAll
            withRemoveAll={false}
            disabled={disabled || ownArrivalsAndDepartures}
            selectedPostCode={selectedPostCode}
            setSelectedPostCode={setSelectedPostCode}
            isOpen={isBusStationsOpen}
            setIsOpen={setIsBusStationsOpen}
            ownArrivalsAndDepartures={ownArrivalsAndDepartures}
          />
        </ScrollTo>
      </div>
    )
  };

  const paxSelectorComponent: DropdownFilterComponent = {
    id: '1',
    component: (
      <div
        className={`dropdown-filter__pax position-relative${
          disabled ? ' dropdown-filter__pax--disabled' : ''
        }`}
      >
        <label className="custom-fieldset-label">
          <PaxSelector
            paxSelection={store.searchParams.occupancy || []}
            setPaxSelection={setFunctions.occupancy}
            withCategorySelect
            onlyAdultSelect
            showLabelOptionsWithDesc
            staticLabel={
              isBusPackage
                ? !!store.searchParams.occupancy
                  ? store.searchParams.occupancy[0].adults
                      .toString()
                      .concat(' ')
                      .concat(
                        t(Keys.person, { count: Number(store.searchParams.occupancy[0].adults) })
                      )
                  : ''
                : t(Keys.persons)
            }
            disabled={disabled}
          />
          <FontAwesomeIcon icon={faAngleDown} />
          <span className="placeholder">{t(Keys.persons)}</span>
        </label>
      </div>
    )
  };
  const changeAdultsCount = (newVal: number) => {
    const apiRoomContainer: ApiRoomContainer = {
      adults: newVal
    } as ApiRoomContainer;

    setFunctions.occupancy([apiRoomContainer]);
  };

  const numberSelectorComponent: DropdownFilterComponent = {
    id: '1',
    component: (
      <NumberSelector
        initial={store.occupancy.length > 0 ? store.occupancy[0].adults : 1}
        collapseAbsolute
        min={1}
        max={10}
        handleSelectionChanged={(selectedNumber: number) => changeAdultsCount(selectedNumber)}
        approveLabel="Übernehmen"
        singleLabel="Person"
        multipleLabel="Personen"
      />
    )
  };

  const geoUnitsSelectorComponent: DropdownFilterComponent = {
    id: '2',
    component: (
      <GeoUnitCheckboxAsync
        itemType={ApiItemType.TRANSPORT}
        airportType={ApiAirportType.ORIGIN}
        value={store.origins}
        onChange={setFunctions.origins}
        withSelectAll
        showPseudo
        withRemoveAll
        staticLabel={t(Keys.airportOfDeparture)}
        possibleGeoUnitCodes={originsProp}
        disabled={disabled}
      />
    )
  };

  const dateRangeSelectorComponent: DropdownFilterComponent = {
    id: '3',
    component: (
      <label className="custom-fieldset-label">
        <DateRangeInputSingleField
          onSubmit={setFunctions.datepicker}
          startDate={store.searchParams.startDate}
          endDate={store.searchParams.endDate}
          minDate={datepickerMinDate}
          maxDate={datepickerMaxDate}
          defaultDate={defaultDate}
          onToggle={onToggle?.calendar}
          ref={dateRangeRef}
          useDateDisplay={isBusPackage}
          alternativeDateLabels={{
            startDate: t(Keys.earliestTravelDate),
            endDate: t(Keys.latestTravelDate),
            duration: t(Keys.duration)
          }}
        />
        {isBusPackage && <span className="placeholder">{t(Keys.travelPeriod)}</span>}
      </label>
    )
  };

  const busFilterSelections: Array<DropdownFilterComponent> = new Array(3);
  busFilterSelections.push(postalCodeSelectorComponent);
  busFilterSelections.push(paxSelectorComponent);
  busFilterSelections.push(dateRangeSelectorComponent);

  const finalPaxSelector = !showNumberSelector ? paxSelectorComponent : numberSelectorComponent;
  const packageFilterSelections: Array<DropdownFilterComponent> = new Array(3);
  packageFilterSelections.push(finalPaxSelector);
  packageFilterSelections.push(geoUnitsSelectorComponent);
  packageFilterSelections.push(dateRangeSelectorComponent);

  return (
    <DropdownFilter
      onChange={setFunctions.busStations}
      selectedOptions={selectedBusStations}
      filterComponents={isBusPackage ? busFilterSelections : packageFilterSelections}
      pills={getPills(store, t, config, appService)}
      onPillClick={handlePillClick}
      showSearchButton={isBusPackage}
    />
  );
});

const isNumber = (value?: number): value is number => {
  return value !== undefined && value !== null;
};

export default PackageDropdownFilterRenderer;
